/**
 * Live API Test Demo
 * 
 * Demonstrates that all AI features use real authentication from the database
 * and make live Graph API calls with stored OAuth tokens.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { blink } from '@/lib/blink';
import { emailActions } from '@/services/emailActions';
import { gpt5NanoAgent } from '@/services/gpt5NanoAgent';
import { getConnectionStatus } from '@/services/oauthService';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader, Database, Cloud, Brain, Zap } from 'lucide-react';

export const LiveAPITestDemo: React.FC = () => {
  const [user, setUser] = useState<any>(null);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState<any[]>([]);
  
  // Test inputs
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('Test from AI Agent');
  const [emailBody, setEmailBody] = useState('This email was sent via live Graph API using stored OAuth tokens.');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    initializeDemo();
  }, []);

  const initializeDemo = async () => {
    try {
      const userData = await blink.auth.me();
      setUser(userData);
      
      const status = await getConnectionStatus(userData.id);
      setConnectionStatus(status);
      
      // Initialize email actions with user
      emailActions.setUserId(userData.id);
      
      console.log('✅ Demo initialized with user:', userData.id);
      console.log('📧 Connection status:', status);
    } catch (error) {
      console.error('Failed to initialize demo:', error);
      toast.error('Please sign in to use this demo');
    }
  };

  const addTestResult = (test: string, success: boolean, details: any) => {
    setTestResults(prev => [{
      test,
      success,
      details,
      timestamp: new Date().toISOString()
    }, ...prev]);
  };

  const testDatabaseAuth = async () => {
    setLoading(true);
    try {
      console.log('🧪 Testing: Database Authentication Retrieval');
      
      // Fetch email accounts from database
      const accounts = await blink.db.emailAccounts.list({
        where: { userId: user.id },
        limit: 10
      });

      console.log('✅ Retrieved', accounts.length, 'email accounts from database');
      
      accounts.forEach((account: any) => {
        console.log('  →', account.provider, ':', account.emailAddress || account.email_address);
        console.log('    Access Token:', account.accessToken?.substring(0, 20) + '...' || account.access_token?.substring(0, 20) + '...');
        console.log('    Refresh Token:', account.refreshToken ? 'Present' : 'None' || account.refresh_token ? 'Present' : 'None');
        console.log('    Expires:', account.tokenExpiresAt || account.token_expires_at);
      });

      addTestResult('Database Auth Retrieval', true, {
        accountsFound: accounts.length,
        providers: accounts.map((a: any) => a.provider),
        hasTokens: accounts.every((a: any) => !!(a.accessToken || a.access_token))
      });

      toast.success(`Found ${accounts.length} connected accounts with valid tokens`);
    } catch (error) {
      console.error('❌ Database auth test failed:', error);
      addTestResult('Database Auth Retrieval', false, { error: error instanceof Error ? error.message : 'Unknown error' });
      toast.error('Failed to retrieve auth from database');
    } finally {
      setLoading(false);
    }
  };

  const testLiveEmailSend = async () => {
    if (!emailTo || !emailSubject || !emailBody) {
      toast.error('Please fill in all email fields');
      return;
    }

    setLoading(true);
    try {
      console.log('🧪 Testing: Live Email Send via Graph API');
      console.log('   Using stored OAuth tokens from database');
      
      const result = await emailActions.sendEmail({
        to: [emailTo],
        subject: emailSubject,
        body: emailBody
      });

      console.log('✅ Email sent successfully via live Graph API');
      console.log('   Message ID:', result.messageId);

      addTestResult('Live Email Send', true, {
        messageId: result.messageId,
        recipient: emailTo,
        usedLiveAPI: true
      });

      toast.success('Email sent successfully via live Graph API!');
    } catch (error) {
      console.error('❌ Live email send failed:', error);
      addTestResult('Live Email Send', false, { error: error instanceof Error ? error.message : 'Unknown error' });
      toast.error('Failed to send email via Graph API');
    } finally {
      setLoading(false);
    }
  };

  const testLiveEmailSearch = async () => {
    if (!searchQuery) {
      toast.error('Please enter a search query');
      return;
    }

    setLoading(true);
    try {
      console.log('🧪 Testing: Live Email Search via Graph API');
      console.log('   Query:', searchQuery);
      console.log('   Using stored OAuth tokens from database');
      
      const results = await emailActions.search({
        query: searchQuery
      });

      console.log('✅ Search completed via live Graph API');
      console.log('   Found:', results.length, 'emails');

      addTestResult('Live Email Search', true, {
        query: searchQuery,
        resultsCount: results.length,
        usedLiveAPI: true
      });

      toast.success(`Found ${results.length} emails via live Graph API!`);
    } catch (error) {
      console.error('❌ Live email search failed:', error);
      addTestResult('Live Email Search', false, { error: error instanceof Error ? error.message : 'Unknown error' });
      toast.error('Failed to search emails via Graph API');
    } finally {
      setLoading(false);
    }
  };

  const testAIAgentIntegration = async () => {
    setLoading(true);
    try {
      console.log('🧪 Testing: AI Agent with Live API Integration');
      console.log('   Command: Search emails from last week');
      
      const response = await gpt5NanoAgent.processUserIntent('Search emails from last week');

      console.log('✅ AI Agent processed intent');
      console.log('   Action:', response.action.type);
      console.log('   Confidence:', response.action.confidence);

      if (response.validation.isValid && response.action.type === 'email_search') {
        console.log('🔄 Executing AI action with live Graph API...');
        const result = await gpt5NanoAgent.executeAction(response.action);
        
        console.log('✅ AI action executed successfully via live Graph API');
        console.log('   Results:', result);

        addTestResult('AI Agent + Live API', true, {
          actionType: response.action.type,
          confidence: response.action.confidence,
          resultsCount: result.resultsCount || 0,
          usedLiveAPI: true
        });

        toast.success(`AI Agent executed search via live Graph API! Found ${result.resultsCount || 0} emails`);
      } else {
        addTestResult('AI Agent + Live API', false, {
          error: 'Invalid action or validation failed',
          validation: response.validation
        });
        toast.error('AI Agent validation failed');
      }
    } catch (error) {
      console.error('❌ AI Agent test failed:', error);
      addTestResult('AI Agent + Live API', false, { error: error instanceof Error ? error.message : 'Unknown error' });
      toast.error('AI Agent test failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-400 to-orange-400">
          <CardHeader>
            <CardTitle className="text-2xl font-bold text-amber-950">
              🔬 Live API Integration Test Suite
            </CardTitle>
            <CardDescription className="text-amber-900">
              Verify all AI features use real authentication and make live Graph API calls
            </CardDescription>
          </CardHeader>
        </Card>

        {/* User Status */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Authentication Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {user ? (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="font-semibold">User ID:</span>
                  <code className="bg-amber-100 px-2 py-1 rounded text-sm">{user.id}</code>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="font-semibold">Email:</span>
                  <span>{user.email}</span>
                </div>
                {connectionStatus && (
                  <div className="space-y-2 mt-4">
                    <div className="font-semibold">Connected Providers:</div>
                    <div className="flex gap-3">
                      <div className={`px-3 py-1 rounded-lg ${connectionStatus.gmail ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        Gmail: {connectionStatus.gmail ? '✓ Connected' : '✗ Not Connected'}
                      </div>
                      <div className={`px-3 py-1 rounded-lg ${connectionStatus.outlook ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                        Outlook: {connectionStatus.outlook ? '✓ Connected' : '✗ Not Connected'}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-amber-600">
                <Loader className="w-4 h-4 animate-spin" />
                Loading user data...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Test 1: Database Auth */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              Test 1: Database Authentication Retrieval
            </CardTitle>
            <CardDescription>
              Verify OAuth tokens are stored and retrieved from database
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={testDatabaseAuth} disabled={loading || !user} className="bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
              Test Database Auth
            </Button>
          </CardContent>
        </Card>

        {/* Test 2: Live Email Send */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5 text-green-600" />
              Test 2: Live Email Send via Graph API
            </CardTitle>
            <CardDescription>
              Send real email using stored OAuth tokens
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              placeholder="Recipient email (e.g., test@example.com)"
              className="border-amber-200"
            />
            <Input
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Email subject"
              className="border-amber-200"
            />
            <Textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Email body"
              rows={4}
              className="border-amber-200"
            />
            <Button onClick={testLiveEmailSend} disabled={loading || !user} className="bg-green-600 hover:bg-green-700">
              {loading ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
              Send Email via Live API
            </Button>
          </CardContent>
        </Card>

        {/* Test 3: Live Email Search */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Test 3: Live Email Search via Graph API
            </CardTitle>
            <CardDescription>
              Search emails using live Graph API with stored tokens
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search query (e.g., from:john@example.com)"
              className="border-amber-200"
            />
            <Button onClick={testLiveEmailSearch} disabled={loading || !user} className="bg-purple-600 hover:bg-purple-700">
              {loading ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
              Search via Live API
            </Button>
          </CardContent>
        </Card>

        {/* Test 4: AI Agent Integration */}
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-orange-600" />
              Test 4: AI Agent + Live API Integration
            </CardTitle>
            <CardDescription>
              Test GPT-5 Nano Agent executing actions via live Graph API
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={testAIAgentIntegration} disabled={loading || !user} className="bg-orange-600 hover:bg-orange-700">
              {loading ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
              Test AI Agent with Live API
            </Button>
          </CardContent>
        </Card>

        {/* Test Results */}
        {testResults.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader>
              <CardTitle>Test Results</CardTitle>
              <CardDescription>Recent test execution logs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {testResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border ${
                      result.success
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {result.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className="font-semibold">{result.test}</span>
                      <span className="text-sm text-gray-500 ml-auto">
                        {new Date(result.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <pre className="text-xs bg-white p-2 rounded overflow-auto">
                      {JSON.stringify(result.details, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documentation */}
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle>📚 How It Works</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <strong className="text-amber-900">1. Authentication Flow:</strong>
              <p className="text-gray-700 ml-4 mt-1">
                OAuth tokens are stored in the <code className="bg-amber-100 px-1 rounded">email_accounts</code> table with <code className="bg-amber-100 px-1 rounded">userId</code>, <code className="bg-amber-100 px-1 rounded">provider</code>, <code className="bg-amber-100 px-1 rounded">accessToken</code>, and <code className="bg-amber-100 px-1 rounded">refreshToken</code>.
              </p>
            </div>
            <div>
              <strong className="text-amber-900">2. Token Retrieval:</strong>
              <p className="text-gray-700 ml-4 mt-1">
                <code className="bg-amber-100 px-1 rounded">getValidAccessToken()</code> fetches tokens from the database, automatically refreshes if expired, and returns valid access tokens for API calls.
              </p>
            </div>
            <div>
              <strong className="text-amber-900">3. Live API Calls:</strong>
              <p className="text-gray-700 ml-4 mt-1">
                All email and calendar operations use these tokens to make real Graph API calls to Microsoft Outlook or Gmail.
              </p>
            </div>
            <div>
              <strong className="text-amber-900">4. AI Integration:</strong>
              <p className="text-gray-700 ml-4 mt-1">
                GPT-5 Nano Agent processes user intents and executes actions by calling <code className="bg-amber-100 px-1 rounded">emailActions</code> service, which uses the stored tokens for live API operations.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LiveAPITestDemo;
